/**
 * GoldenBookNotes — Keeper Edit Desk (19 Jul 2026).
 * The four Keepers rewrite any Golden Book margin note in their own
 * words. Reads GET /api/os-admin/goldenbook/notes · writes
 * POST /api/os-admin/goldenbook/notes/{hl_id}.
 */
import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";

const KEEPERS = ["dipali", "nisha", "samar", "srikanth"];

export default function GoldenBookNotes() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, has_more: false, role: "read_only" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ q: "", keeper: null, city: "", page: 1 });
  const [drafts, setDrafts] = useState({}); // hl_id → text
  const [busy, setBusy] = useState(null);
  const [flash, setFlash] = useState(null); // { id, ok, msg }

  const load = async (f = filters) => {
    setLoading(true);
    setError("");
    try {
      const p = new URLSearchParams();
      if (f.q.trim()) p.set("q", f.q.trim());
      if (f.keeper) p.set("keeper", f.keeper);
      if (f.city.trim()) p.set("city", f.city.trim());
      p.set("page", String(f.page));
      const r = await api.get(`/os-admin/goldenbook/notes?${p.toString()}`);
      setRows(r.data.rows || []);
      setMeta({ total: r.data.total || 0, page: r.data.page || 1,
                has_more: !!r.data.has_more, role: r.data.role || "read_only" });
      setDrafts({});
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Could not open the Book.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(filters); /* eslint-disable-next-line */ }, [filters]);

  const save = async (id) => {
    const note = (drafts[id] || "").trim();
    if (note.length < 3) return;
    setBusy(id);
    setFlash(null);
    try {
      await api.post(`/os-admin/goldenbook/notes/${id}`, { note });
      setRows((prev) => prev.map((r) => (r.hl_id === id
        ? { ...r, keeper_note: note, note_edited_by: "you" } : r)));
      setDrafts((d) => { const n = { ...d }; delete n[id]; return n; });
      setFlash({ id, ok: true, msg: "Inscribed" });
    } catch (e) {
      setFlash({ id, ok: false, msg: formatApiError(e?.response?.data?.detail) || "Save failed" });
    } finally {
      setBusy(null);
    }
  };

  const isReadOnly = meta.role === "read_only";

  return (
    <div className="os-fyi-queue" data-testid="os-gbnotes-page">
      <div className="os-pagehead">
        <h1>Keeper Edit Desk</h1>
        <span className="count">{loading ? "Loading…" : `${meta.total} inscriptions`}</span>
      </div>
      <div className="os-pagesub">
        The Golden Book's margin notes · rewrite any note in your own words —
        real notes over time make the Book unfakeable.
      </div>

      <div className="os-fyi-filterblock" data-testid="os-gbnotes-filters">
        <div className="os-toolbar">
          <span className="os-fyi-label">Keeper</span>
          <button type="button" className={`os-fchip ${!filters.keeper ? "on" : ""}`}
                  onClick={() => setFilters((p) => ({ ...p, keeper: null, page: 1 }))}
                  data-testid="os-gbnotes-keeper-all">all</button>
          {KEEPERS.map((k) => (
            <button key={k} type="button"
                    className={`os-fchip ${filters.keeper === k ? "on" : ""}`}
                    onClick={() => setFilters((p) => ({ ...p, keeper: k, page: 1 }))}
                    data-testid={`os-gbnotes-keeper-${k}`}>{k}</button>
          ))}
          <span className="os-spacer" />
          <input type="text" placeholder="City (e.g. Mumbai)" value={filters.city}
                 onChange={(e) => setFilters((p) => ({ ...p, city: e.target.value, page: 1 }))}
                 maxLength={60} className="os-fyi-search" style={{ maxWidth: 160 }}
                 data-testid="os-gbnotes-city" />
          <input type="search" placeholder="Search name · note" value={filters.q}
                 onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value, page: 1 }))}
                 maxLength={80} className="os-fyi-search"
                 data-testid="os-gbnotes-search" />
        </div>
      </div>

      {error ? (
        <div className="os-fyi-msg os-fyi-msg-err" data-testid="os-gbnotes-error">{error}</div>
      ) : rows.length === 0 && !loading ? (
        <div className="os-fyi-msg" data-testid="os-gbnotes-empty">No inscriptions match.</div>
      ) : (
        <table className="os-tbl" data-testid="os-gbnotes-table">
          <thead>
            <tr>
              <th>Entry</th>
              <th>Keeper</th>
              <th style={{ width: "44%" }}>Margin note</th>
              {!isReadOnly && <th />}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.hl_id} data-testid={`os-gbnotes-row-${r.hl_id}`}>
                <td>
                  <div className="os-fyi-title" style={{ maxWidth: 220 }}>{r.name}</div>
                  <div className="os-fyi-sub">
                    {[r.city, (r.category || "").replace("_", " "),
                      r.rating ? `★${r.rating}` : null].filter(Boolean).join(" · ")}
                  </div>
                </td>
                <td><span className="os-pill os-p-prog">{r.keeper}</span></td>
                <td>
                  {isReadOnly ? (
                    <div className="os-fyi-sub" style={{ maxWidth: 360, fontStyle: "italic" }}>
                      “{r.keeper_note || "—"}”
                    </div>
                  ) : (
                    <textarea
                      value={drafts[r.hl_id] !== undefined ? drafts[r.hl_id] : (r.keeper_note || "")}
                      onChange={(e) => setDrafts((d) => ({ ...d, [r.hl_id]: e.target.value }))}
                      maxLength={300} rows={2}
                      className="os-fyi-search"
                      style={{ width: "100%", resize: "vertical", fontStyle: "italic" }}
                      data-testid={`os-gbnotes-input-${r.hl_id}`} />
                  )}
                  {r.note_edited_by && (
                    <div className="os-fyi-sub" style={{ marginTop: 3 }}>
                      rewritten by {r.note_edited_by}
                    </div>
                  )}
                  {flash && flash.id === r.hl_id && (
                    <div className={flash.ok ? "os-fyi-sub" : "os-fyi-sub os-fyi-msg-err"}
                         data-testid={`os-gbnotes-flash-${r.hl_id}`}>
                      {flash.msg}
                    </div>
                  )}
                </td>
                {!isReadOnly && (
                  <td>
                    <button type="button" className="os-btn-ghost"
                            disabled={busy === r.hl_id
                              || drafts[r.hl_id] === undefined
                              || (drafts[r.hl_id] || "").trim().length < 3
                              || drafts[r.hl_id] === (r.keeper_note || "")}
                            onClick={() => save(r.hl_id)}
                            data-testid={`os-gbnotes-save-${r.hl_id}`}>
                      {busy === r.hl_id ? "Saving…" : "Inscribe"}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="os-fyi-pagi" data-testid="os-gbnotes-pagination">
        <button type="button" className="os-btn-ghost"
                disabled={meta.page <= 1 || loading}
                onClick={() => setFilters((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
                data-testid="os-gbnotes-page-prev">‹ Prev</button>
        <span className="os-fyi-pagemark">Page {meta.page}</span>
        <button type="button" className="os-btn-ghost"
                disabled={!meta.has_more || loading}
                onClick={() => setFilters((p) => ({ ...p, page: p.page + 1 }))}
                data-testid="os-gbnotes-page-next">Next ›</button>
      </div>
    </div>
  );
}
